import { useEffect, useRef, useState } from "react";
import { CheckCircle2, FileText, UploadCloud, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { useReceipts } from "../../lib/receipts/useReceipts";

interface ReceiptUploadDialogProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  linkedExpenseId?: string;
  linkedInvoiceId?: string;
}

const ACCEPT = "image/*,application/pdf";

export function ReceiptUploadDialog({
  open,
  onOpenChange,
  linkedExpenseId,
  linkedInvoiceId,
}: ReceiptUploadDialogProps) {
  const { add } = useReceipts();
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());
  const [uploadStatus, setUploadStatus] = useState<Map<string, "pending" | "uploading" | "done" | "error">>(new Map());
  const [uploadErrors, setUploadErrors] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset state whenever the dialog is closed.
  useEffect(() => {
    if (!open) {
      setFiles([]);
      setPreviews(new Map());
      setUploadStatus(new Map());
      setUploadErrors(new Map());
      setError(null);
      setUploading(false);
      setDragActive(false);
    }
  }, [open]);

  // Build/release preview object URLs for all image files.
  useEffect(() => {
    const newPreviews = new Map<string, string>();
    const urls: string[] = [];
    for (const f of files) {
      if (f.type.startsWith("image/")) {
        const url = URL.createObjectURL(f);
        newPreviews.set(f.name, url);
        urls.push(url);
      }
    }
    setPreviews(newPreviews);
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [files]);

  function handleFiles(fileList: FileList | null) {
    setError(null);
    if (!fileList || fileList.length === 0) return;
    const valid: File[] = [];
    for (let i = 0; i < fileList.length; i++) {
      const f = fileList[i];
      if (!f) continue;
      const isImage = f.type.startsWith("image/");
      const isPdf = f.type === "application/pdf";
      if (!isImage && !isPdf) {
        setError(`Unsupported file type: ${f.name}. Please upload images or PDFs.`);
        continue;
      }
      valid.push(f);
    }
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
    }
  }

  function removeFile(f: File) {
    setFiles(prev => prev.filter(x => x !== f));
  }

  async function handleUpload() {
    if (files.length === 0) {
      setError("Please select at least one file.");
      return;
    }
    setUploading(true);
    setError(null);
    const newStatus = new Map(uploadStatus);
    const newErrors = new Map(uploadErrors);
    let anyFailed = false;
    for (const f of files) {
      if (newStatus.get(f.name) === "done") continue; // skip already uploaded
      newStatus.set(f.name, "uploading");
      setUploadStatus(new Map(newStatus));
      try {
        await add({ file: f, linkedExpenseId, linkedInvoiceId });
        newStatus.set(f.name, "done");
      } catch (err) {
        newStatus.set(f.name, "error");
        newErrors.set(f.name, err instanceof Error ? err.message : "Upload failed");
        anyFailed = true;
      }
      setUploadStatus(new Map(newStatus));
    }
    setUploadErrors(new Map(newErrors));
    setUploading(false);
    if (!anyFailed) {
      onOpenChange(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="receipt-upload-dialog"
        className="sm:max-w-md"
      >
        <DialogHeader>
          <DialogTitle>Upload receipts</DialogTitle>
          <DialogDescription>
            Attach images or PDFs. Files are stored privately in your account.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          <label
            onDragEnter={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
            onDrop={(e) => {
              e.preventDefault();
              setDragActive(false);
              handleFiles(e.dataTransfer.files);
            }}
            className={`flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${
              dragActive
                ? "border-blue-400 bg-blue-50"
                : "border-slate-200 bg-slate-50 hover:border-slate-300"
            }`}
          >
            <UploadCloud className="w-8 h-8 text-slate-400" />
            <div className="text-sm text-slate-700 font-medium">
              Drag & drop files here, or click to browse
            </div>
            <div className="text-xs text-slate-500">
              Images (PNG, JPG) or PDF — select multiple
            </div>
            <input
              ref={inputRef}
              data-testid="receipt-upload-input"
              type="file"
              accept={ACCEPT}
              multiple
              className="sr-only"
              onChange={(e) => handleFiles(e.target.files)}
            />
          </label>

          {files.length > 0 && (
            <div className="flex flex-col gap-2 max-h-48 overflow-y-auto">
              {files.map((f, i) => {
                const status = uploadStatus.get(f.name) ?? "pending";
                const preview = previews.get(f.name);
                return (
                  <div
                    key={f.name + i}
                    data-testid={`receipt-upload-file-${i}`}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3"
                  >
                    <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center shrink-0">
                      {f.type.startsWith("image/") && preview ? (
                        <img src={preview} alt="" className="object-cover w-full h-full" />
                      ) : (
                        <FileText className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-slate-900 truncate">{f.name}</div>
                      <div className="text-xs text-slate-500">
                        {f.type === "application/pdf" ? "PDF" : "Image"} · {(f.size / 1024).toFixed(1)} KB
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {status === "uploading" && <span className="text-xs text-blue-600">Uploading…</span>}
                      {status === "done" && <CheckCircle2 className="w-4 h-4 text-green-500" />}
                      {status === "error" && <span className="text-xs text-red-500">Failed</span>}
                      {status === "pending" && (
                        <button
                          type="button"
                          onClick={() => removeFile(f)}
                          className="p-1 text-slate-400 hover:text-slate-600 rounded"
                          aria-label={`Remove ${f.name}`}
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {error && (
            <div className="text-xs font-medium text-red-600">{error}</div>
          )}
        </div>

        <DialogFooter>
          {files.length > 0 && (
            <span
              data-testid="receipt-upload-file-count"
              className="text-xs text-slate-500 mr-auto"
            >
              {files.length} file{files.length === 1 ? "" : "s"} selected
            </span>
          )}
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-md transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            data-testid="receipt-upload-submit"
            onClick={handleUpload}
            disabled={files.length === 0 || uploading}
            className="px-4 py-2 text-sm font-medium text-white bg-[#0070F2] hover:bg-[#005BCA] disabled:bg-slate-300 disabled:cursor-not-allowed rounded-md transition-colors"
          >
            {uploading ? "Uploading…" : "Upload"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
